<template>
  <div class="main__content content">
    <div class="pacs-settings-table" />

    <SpinnerLoading
      v-if="isLoadingTable || isLoadingCameras"
      class="loader_center"
      color="blue"
    />
    <PacsTable
      v-else
      :column-captions="columnCaptions"
      :column-names="columnNames"
      :data-rows="dataRows"
      :page-count="pageCount"
      :key-field="keyField"
      :sort-info="sortInfo"
      :total-count="totalCount"
      :current-page="currentPage"
      :custom-row-mode="'row'"
      :view-only-mode="true"
      @new-page="selectPage"
      @new-sort="changeSort"
    >
      <template #customTdCells="{ dataRow }">
        <div class="cam-row" @click="openOneScreenDialogFromRow(dataRow)">
          <div class="cam-row__thumb">
            <img :src="getRowScreenshotUrl(dataRow)" alt="" loading="lazy">
          </div>

          <div class="cam-row__meta">
            <div class="cam-row__title">
              {{ getRowCameraTitle(dataRow) }}
            </div>
            <div class="cam-row__subtitle">
              {{ getRowCameraAddress(dataRow) }}
            </div>
          </div>

          <div class="cam-row__actions" @click.stop>
            <button
              class="cam-row__action cam-row__action--expand"
              type="button"
              @click="openDialogEditZonesFromRow(dataRow)"
            >
              <svg class="cam-row__icon" aria-hidden="true">
                <use xlink:href="#icon-menu-zones" />
              </svg>
              <span class="cam-row__label">Настройка</span>
            </button>
            <button
              class="cam-row__action cam-row__action--expand"
              type="button"
              @click="openCrowdReportFromRow(dataRow)"
            >
              <svg class="cam-row__icon" aria-hidden="true">
                <use xlink:href="#icon-menu-report" />
              </svg>
              <span class="cam-row__label">Отчет</span>
            </button>
          </div>
        </div>
      </template>
    </PacsTable>
  </div>
</template>

<script>
import PacsTable from "@/components/pacs/PacsTable.vue";
import {
  ROUTE_PACS_EDIT_CROWD_ANALYTICS,
  ROUTE_CROWD_REPORT,
} from "@/router/names.js";
import {
  ACTION_LOAD_CROWD_ANALYTICS_FOR_TABLE,
  CAMERA_ANALYTICS_STRUCT_CROWD,
  ACTION_EDIT_CROWD_ANALYTICS
} from "@/store/analytics/index.js";
import {entityTableMixin} from "@/components/pacs/mixins.js";
import {entityAnalyticsTableMixin} from "@/components/pacs/analytics/mixins.js";
import {
  FIELDS_CAMERA
} from "@/store/cameras/index.js";
import {ACTION_LOAD_CAMERA_FOR_EDIT_ANALYTICS} from "@/store/cameras/index.js";
import {TOKEN_TTL} from "@/utils/consts.js";
import RequestTokenCameraDialog from "@/components/pacs/analytics/RequestTokenCameraDialog.vue";
import {AnalyticZonesConfig, AnalyticZone} from "camsng-frontend-shared/components/smartPlayer/mixins.js";

/**
 * Компонент отображения таблицы камер с аналитикой по толпе.
 */
export default {
  name: "TableCrowdAnalytics",
  mixins: [
    entityTableMixin,
    entityAnalyticsTableMixin,
  ],
  components: {
    PacsTable
  },
  data() {
    return {
      entityStruct: CAMERA_ANALYTICS_STRUCT_CROWD,
    };
  },
  methods: {
    async loadPage() {
      this.isLoadingTable = true;
      try {
        const responseData = await this.$store.dispatch(`analytics/${ACTION_LOAD_CROWD_ANALYTICS_FOR_TABLE}`, {
          search: this.searchText,
          page: this.currentPage,
        });
        this.pageCount = responseData.page.all;
        this.totalCount = responseData.count;
        const cameraNumbers = responseData.results.map(
          row => row[CAMERA_ANALYTICS_STRUCT_CROWD.fields.camera_number.name]
        );
        await this.loadCamerasInfo(cameraNumbers);

        this.dataRows = responseData.results.map((rawInfo) => {
          const cameraNumber = rawInfo[CAMERA_ANALYTICS_STRUCT_CROWD.fields.camera_number.name];

          return {
            ...this.calcDefaultDataRow(rawInfo),
            __raw: rawInfo,
            [CAMERA_ANALYTICS_STRUCT_CROWD.fields.camera_number.name]: {
              type: CAMERA_ANALYTICS_STRUCT_CROWD.fields.camera_number.type,
              value: cameraNumber,
              params: {name: ROUTE_PACS_EDIT_CROWD_ANALYTICS, params: {cameraNumber}}
            },
            camera_title: {type: "string", value: (this.camerasInfo[cameraNumber]?.title ?? "—")},
            camera_address: {type: "string", value: (this.camerasInfo[cameraNumber]?.address ?? "—")},
          };
        });
      } finally {
        this.isLoadingTable = false;
      }
    },
    openCrowdReportFromRow(dataRow) {
      const raw = dataRow.__raw;
      const cameraNumber = raw[CAMERA_ANALYTICS_STRUCT_CROWD.fields.camera_number.name];

      this.$router.push({
        name: ROUTE_CROWD_REPORT,
        params: {cameraNumber},
      });
    },
    async openDialogEditZonesFromRow(dataRow) {
      const raw = dataRow.__raw;
      const cameraNumber = raw[CAMERA_ANALYTICS_STRUCT_CROWD.fields.camera_number.name];

      const zones = raw[CAMERA_ANALYTICS_STRUCT_CROWD.fields.zones.name] || [];
      const imageWidth = raw[CAMERA_ANALYTICS_STRUCT_CROWD.fields.image_width.name];
      const imageHeight = raw[CAMERA_ANALYTICS_STRUCT_CROWD.fields.image_height.name];
      const minConfidence = raw[CAMERA_ANALYTICS_STRUCT_CROWD.fields.min_confidence.name];
      const maxPeopleCount = raw[CAMERA_ANALYTICS_STRUCT_CROWD.fields.max_people_count.name];

      try {
        const response = await this.$store.dispatch(`cameras/${ACTION_LOAD_CAMERA_FOR_EDIT_ANALYTICS}`, {
          fields: [
            FIELDS_CAMERA.number,
            FIELDS_CAMERA.address,
            FIELDS_CAMERA.title,
            FIELDS_CAMERA.is_embed,
            FIELDS_CAMERA.analytics,
            FIELDS_CAMERA.is_fav,
            FIELDS_CAMERA.is_public,
            FIELDS_CAMERA.server,
            FIELDS_CAMERA.streams_count,
            FIELDS_CAMERA.token_l,
            FIELDS_CAMERA.token_r,
            FIELDS_CAMERA.token_d,
            FIELDS_CAMERA.permission,
          ],
          tokenDVRTTL: TOKEN_TTL.MULTI,
          tokenDownloadStart: 1,
          numbers: [cameraNumber],
        });

        const cameraInfo = response.data.results[0];

        const analyticZonesConfig = new AnalyticZonesConfig(
          imageWidth,
          imageHeight,
          3,
          100,
          zones.map((rawZone) => new AnalyticZone(rawZone.coordinates, rawZone.sensitivity)),
          maxPeopleCount,
        );

        this.$camsdals.open(
          RequestTokenCameraDialog,
          {
            archiveToken: cameraInfo.token_r,
            downloadToken: cameraInfo.token_d,
            liveToken: cameraInfo.token_l,
            cameraNumber,
            title: cameraInfo.title,
            domain: cameraInfo.server.domain,
            streamCount: cameraInfo.streams_count,
            hasSound: cameraInfo.is_sounding,
            vendorName: cameraInfo.server.vendor_name,
            analyticZonesConfig: analyticZonesConfig,
          },
          {dialogTitle: `Просмотр видео с камеры №${cameraNumber} - ${cameraInfo.title}`},
          {
            name: "js-click-outside",
            size: "lg",
            onClose: async (analyticZonesConfigForSave) => {
              if (!analyticZonesConfigForSave) return;

              const payload = {
                cameraNumber,
                minConfidence,
                maxPeopleCount: analyticZonesConfigForSave.maxPeopleCount,
                imageWidth: analyticZonesConfigForSave.zonesWidth,
                imageHeight: analyticZonesConfigForSave.zonesHeight,
                zones: analyticZonesConfigForSave.exportZones().map((analyticZone) => {
                  return {coordinates: analyticZone.exportPoints(), sensitivity: analyticZone.sensitivity};
                }),
              };
              await this.$store.dispatch(`analytics/${ACTION_EDIT_CROWD_ANALYTICS}`, payload);
              await this.loadPage();
            },
          }
        );
      } finally {
        //
      }
    }
  },
};
</script>
<style lang="scss">
@use "../analytic-table-row.scss";
</style>
